import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  confirmSubscriptionCheckout,
  redirectToBilling,
  resumeSubscription,
} from '../api/subscriptions';
import AuthLayout from '../components/AuthLayout';
import AppButton from '../components/common/AppButton';
import { useAuth } from '../context/AuthContext';
import type { PlanId } from '../types/subscription';

export default function SubscriptionCheckoutPage({
  resume = false,
}: {
  resume?: boolean;
}) {
  const { company, user, userCompanies, switchCompany, hasPermission } =
    useAuth();
  const companyId = company?.id;
  const companyOwnerId = company?.ownerId;
  const userId = user?.uid;
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(0);
  const [confirmation, setConfirmation] = useState<{
    companyId: string;
    plan?: PlanId;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [delayed, setDelayed] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    const stored = sessionStorage.getItem(`billing-company-${sessionId}`);
    if (
      stored &&
      stored !== company?.id &&
      userCompanies.some(
        (entry) =>
          entry.company.id === stored && entry.company.ownerId === user?.uid
      )
    ) {
      switchCompany(stored);
    }
  }, [sessionId, company?.id, userCompanies, user?.uid, switchCompany]);

  useEffect(() => {
    if (!sessionId || !companyId || companyOwnerId !== userId) return;
    let disposed = false;
    setConfirmation(null);
    setError(null);
    setPaymentUrl(null);
    setDelayed(false);
    setProcessing(true);
    void (async () => {
      try {
        if (resume) {
          const result = await resumeSubscription(companyId, sessionId);
          if (disposed) return;
          if (result.paymentUrl) {
            setPaymentUrl(result.paymentUrl);
            return;
          }
          if (result.status !== 'active')
            throw new Error(
              'Payment is still pending. Check again after completing payment.'
            );
          setConfirmation({ companyId });
        } else {
          const result = await confirmSubscriptionCheckout(
            companyId,
            sessionId
          );
          if (disposed) return;
          if (result.status !== 'complete')
            throw new Error(
              result.status === 'expired'
                ? 'This checkout has expired.'
                : 'Checkout has not completed yet.'
            );
          if (
            !result.plan ||
            !['starter', 'professional', 'business'].includes(result.plan)
          )
            throw new Error(
              'Unable to verify the purchased plan. Please contact support.'
            );
          setConfirmation({ companyId, plan: result.plan });
        }
      } catch (cause) {
        if (!disposed)
          setError(
            cause instanceof Error
              ? cause.message
              : 'Unable to confirm billing. Please try again.'
          );
      } finally {
        if (!disposed) setProcessing(false);
      }
    })();
    return () => {
      disposed = true;
    };
  }, [sessionId, companyId, companyOwnerId, userId, resume, attempt]);

  useEffect(() => {
    if (!confirmation || confirmation.companyId !== company?.id) return;
    if (
      company.subscription?.isActive &&
      company.subscription.tier &&
      (!confirmation.plan || company.subscription.tier === confirmation.plan)
    ) {
      navigate('/subscription/success', {
        replace: true,
        state: {
          returningSubscriber:
            resume ||
            sessionStorage.getItem(`billing-returning-${company.id}`) ===
              'true',
        },
      });
      return;
    }
    const timer = setTimeout(() => setDelayed(true), 30000);
    return () => clearTimeout(timer);
  }, [confirmation, company, navigate, resume]);

  return (
    <AuthLayout
      title={
        resume ? 'Resuming your subscription' : 'Confirming your subscription'
      }
    >
      <div className="flex w-full flex-col items-center gap-6 py-4 text-center">
        {!sessionId ? (
          <p role="alert">
            The checkout session is missing. Return to subscription settings to
            continue.
          </p>
        ) : !company ? (
          <>
            <p>Select the company used for checkout.</p>
            {userCompanies
              .filter((entry) => entry.company.ownerId === user?.uid)
              .map((entry) => (
                <AppButton
                  key={entry.company.id}
                  onClick={() => switchCompany(entry.company.id)}
                >
                  {entry.company.name}
                </AppButton>
              ))}
          </>
        ) : !hasPermission('canManageSubscription') ? (
          <p role="alert">
            Only the company owner can confirm this subscription.
          </p>
        ) : (
          <>
            {(processing || (confirmation && !delayed)) && (
              <div
                className="h-10 w-10 animate-spin rounded-full border-[3px] border-primary/20 border-t-primary"
                aria-hidden="true"
              />
            )}
            {processing && <p role="status">Checking checkout…</p>}
            {confirmation && (
              <p role="status">
                {delayed
                  ? 'Activation is delayed. You do not need to purchase again. Check again or refresh this page.'
                  : 'Checkout confirmed. Waiting for your subscription to activate…'}
              </p>
            )}
            {error && <p role="alert">{error}</p>}
            {paymentUrl && (
              <>
                <p>Complete payment to resume your subscription.</p>
                <AppButton onClick={() => redirectToBilling(paymentUrl)}>
                  Complete payment
                </AppButton>
              </>
            )}
            {(error || delayed || paymentUrl) && (
              <AppButton
                disabled={processing}
                onClick={() => setAttempt((value) => value + 1)}
              >
                Check again
              </AppButton>
            )}
          </>
        )}
        {!processing &&
          (error ||
            delayed ||
            !sessionId ||
            (company && !hasPermission('canManageSubscription'))) && (
            <AppButton
              variant="tertiary"
              onClick={() => navigate('/onboarding/subscription')}
            >
              Back to subscription settings
            </AppButton>
          )}
      </div>
    </AuthLayout>
  );
}
